import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Textarea,
  FormErrorMessage,
  useColorModeValue,
  HStack,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
} from "@chakra-ui/react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

const UpdateBalanceModal = ({ isOpen, onClose, onSubmit, remainingAmount, initialValues }) => {
  console.log("🚀 ~ UpdateBalanceModal ~ initialValues:", initialValues)
  const updateBalanceSchema = Yup.object().shape({
    collectedAmount: Yup.number()
      .required("Amount is required")
      .moreThan(0, "Amount must be greater than 0")
      .test(
        "remaining-balance",
        `Amount cannot exceed the remaining balance of ${remainingAmount}`,
        function(value) {
          // Skip this validation if we're in edit mode (initialValues exist)
          if (initialValues) return true;
          return value <= remainingAmount;
        }
      )
      .test(
        "is-decimal",
        "Amount cannot have more than 2 decimal places",
        (value) => {
          if (!value) return true;
          return /^\d+(\.\d{0,2})?$/.test(value.toString());
        }
      ),
    transactionId: Yup.string().required("Transaction ID is required"),
    paymentDate: Yup.string().required("Payment Date is required"),
    notes: Yup.string().required("Notes are required"),
  });

  // Parse amount by removing currency symbol and converting to number
  const parseAmount = (amount) => {
    if (typeof amount === 'string') {
      return amount.replace(/[^0-9.-]+/g, '');
    }
    return amount;
  };

  // Format date for input field
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      // Handle DD/MM/YYYY format
      if (dateString.includes('/')) {
        const [day, month, year] = dateString.split('/');
        return new Date(year, month - 1, day).toISOString();
      }
      // Handle ISO date string
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toISOString();
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  const handleDateChange = (date, fieldName) => {
    if (!date) {
      formik.setFieldValue(fieldName, '');
      return;
    }
    try {
      formik.setFieldValue(fieldName, date.toISOString());
    } catch (error) {
      console.error('Error handling date change:', error);
      formik.setFieldValue(fieldName, '');
    }
  };

  const formik = useFormik({
    initialValues: initialValues ? {
      collectedAmount: parseAmount(initialValues.collectedAmount),
      transactionId: initialValues.transactionId || '',
      paymentDate: initialValues.paymentDate ? formatDateForInput(initialValues.paymentDate) : '',
      notes: initialValues.notes || ''
    } : {
      collectedAmount: "",
      transactionId: "",
      paymentDate: "",
      notes: "",
    },
    validationSchema: updateBalanceSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        await onSubmit({
          ...values,
          collectedAmount: Number(values.collectedAmount),
        });
        formik.resetForm();
        onClose();
      } catch (error) {
        console.error("Error updating balance:", error);
      }
    },
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent>
        <ModalHeader>Update Balance</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={formik.handleSubmit}>
          <ModalBody>
            <VStack spacing={4}>
              <HStack spacing={4} width="100%">
                <FormControl
                  isRequired
                  isInvalid={
                    formik.touched.collectedAmount &&
                    formik.errors.collectedAmount
                  }>
                  <FormLabel>Amount</FormLabel>
                  <Input
                    name="collectedAmount"
                    type="number"
                    step="0.01"
                    placeholder="Enter amount"
                    value={formik.values.collectedAmount}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <FormErrorMessage>{formik.errors.collectedAmount}</FormErrorMessage>
                </FormControl>

                <FormControl
                  isRequired
                  isInvalid={
                    formik.touched.transactionId &&
                    formik.errors.transactionId
                  }>
                  <FormLabel>Transaction ID</FormLabel>
                  <Input
                    name="transactionId"
                    placeholder="Enter transaction ID"
                    value={formik.values.transactionId}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <FormErrorMessage>{formik.errors.transactionId}</FormErrorMessage>
                </FormControl>
              </HStack>

              <FormControl
                isRequired
                isInvalid={
                  formik.touched.paymentDate && formik.errors.paymentDate
                }>
                <FormLabel>Payment Date</FormLabel>
                <Popover>
                  <PopoverTrigger>
                    <Input
                      name="paymentDate"
                      value={formatDateForDisplay(formik.values.paymentDate)}
                      placeholder="dd/mm/yyyy"
                      readOnly
                      onClick={(e) => e.target.blur()}
                    />
                  </PopoverTrigger>
                  <PopoverContent width="auto" p={0}>
                    <PopoverBody>
                      <Calendar
                        onChange={(date) => handleDateChange(date, 'paymentDate')}
                        value={formik.values.paymentDate ? new Date(formik.values.paymentDate) : undefined}
                        locale="en-GB"
                        maxDate={new Date()}
                      />
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
                <FormErrorMessage>{formik.errors.paymentDate}</FormErrorMessage>
              </FormControl>

              <FormControl
                isRequired
                isInvalid={formik.touched.notes && formik.errors.notes}>
                <FormLabel>Notes</FormLabel>
                <Textarea
                  name="notes"
                  placeholder="Enter notes"
                  value={formik.values.notes}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  rows={4}
                />
                <FormErrorMessage>{formik.errors.notes}</FormErrorMessage>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Close
            </Button>
            <Button
              colorScheme="purple"
              type="submit"
              isLoading={formik.isSubmitting}>
              Save changes
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default UpdateBalanceModal;
